/// <reference types="node" />
import axiosInstance from '../utils/axios';

declare global {
  namespace NodeJS {
    interface ProcessEnv {
      REACT_APP_WSO2_CLIENT_ID: string;
      REACT_APP_WSO2_CALLBACK_URL: string;
      REACT_APP_WSO2_LOGIN_URL: string;
    }
  }
}

const AUTH_TOKEN_KEY = 'access_token';

export interface User {
  id: string;
  username: string;
  fullname: string;
  email: string;
  roles: string[];
  is_active: boolean;
}

interface LoginResponse {
  token: string;
  user: User;
}

interface ValidateResponse {
  user: User;
}

class AuthService {
  private static instance: AuthService;
  private currentUser: User | null = null;
  private currentRole: { code: string; name: string } | null = null;

  private constructor() {
    // Khôi phục current role từ localStorage
    const storedRole = localStorage.getItem('currentRole');
    if (storedRole) {
      this.currentRole = JSON.parse(storedRole);
    }
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public async getCurrentUser(): Promise<User | null> {
    if (this.currentUser) {
      return this.currentUser;
    }

    try {
      const response = await axiosInstance.get('/auth/user');
      this.currentUser = response.data;
      return this.currentUser;
    } catch (error) {

      return null;
    }
  }

  public async logout(): Promise<void> {
    try {
      // Đợi request logout hoàn tất
      await axiosInstance.post('/auth/logout');
      
      // Xóa tất cả thông tin xác thực
      this.currentUser = null;
      this.currentRole = null;
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem('currentRole');
      
      // Xóa cache của axios
      axiosInstance.defaults.headers.common['Authorization'] = '';
      
      // Đợi một chút để đảm bảo các thao tác xóa hoàn tất
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Chuyển hướng về trang chủ
      window.location.href = '/';
    } catch (error) {

      // Nếu có lỗi, vẫn chuyển hướng về trang chủ
      window.location.href = '/';
    }
  }

  public getLoginUrl(): string {
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: process.env.REACT_APP_WSO2_CLIENT_ID || '',
      redirect_uri: process.env.REACT_APP_WSO2_CALLBACK_URL || '',
      scope: 'openid profile email',
    });

    return `${process.env.REACT_APP_WSO2_LOGIN_URL}?${params.toString()}`;
  }

  public async handleCallback(code: string): Promise<void> {
    try {
      // Exchange code for token with backend
      const response = await axiosInstance.post<LoginResponse>('/auth/callback', { code });
      const { token } = response.data;
      
      // Store token
      localStorage.setItem(AUTH_TOKEN_KEY, token);
      
      // Get user info
      await this.loadUserInfo();
    } catch (error) {

      throw error;
    }
  }

  public async loadUserInfo(): Promise<User> {
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) {
        throw new Error('No auth token found');
      }

      const response = await axiosInstance.get<ValidateResponse>('/auth/validate', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.data.user) {
        throw new Error('No user data received');
      }

      this.currentUser = response.data.user;
      return this.currentUser;
    } catch (error) {

      throw error;
    }
  }

  public getUser(): User | null {
    return this.currentUser;
  }

  public isAuthenticated(): boolean {
    return !!localStorage.getItem(AUTH_TOKEN_KEY);
  }

  public getAuthHeader(): { Authorization?: string } {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async login(code: string): Promise<void> {
    try {
      const response = await axiosInstance.post<LoginResponse>('/auth/login', { code });
      const { user, token } = response.data;
      
      this.currentUser = user;
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    } catch (error) {

      throw error;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  hasRole(role: string): boolean {
    return this.currentUser?.roles.includes(role) || false;
  }

  public getCurrentRole(): { code: string; name: string } | null {
    return this.currentRole;
  }

  public setCurrentRole(role: { code: string; name: string }): void {
    this.currentRole = role;
    localStorage.setItem('currentRole', JSON.stringify(role));
  }
}

export const authService = AuthService.getInstance();

// Không cần thêm interceptor ở đây vì utils/axios.ts đã xử lý token với Bearer prefix
// Interceptor trong utils/axios.ts sẽ tự động thêm Authorization header với Bearer prefix 